import {hasher} from 'hasher';
import {_} from 'lodash';
import * as ko from "knockout";
import PromiseRequest from "../../../req/PromiseRequest";
import {ErrorTracker} from "../../../error.tracker";
import {FormAbstract} from "./FormAbstract";
import {ICompanyLogin, IFormTemplate, IMessageData, ISyncField, TAuthenticationType} from '../../../index';
import {IRequestObject} from "../../../req/index";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import {ObjectTypeId, SPINNER} from "../../../constants";
import {PageData} from "./PageData";
import {convertFromServerString, convertToServerString, validateServerDate} from "../../../util/datetime";
import {createErrorMessage, getNextId, isBit} from "../../../util/misc";
import {config, global} from "../../../appContext";
import {nsSyncFields} from "../../../req/nsSyncFields";
import {FormInput} from "./FormInput";
import {REDIRECTING_MESSAGE, REQUEST_MESSAGE} from "../../../constants/Language";
import {moment} from 'moment';
import {AuthenticationTypeFlag} from "../../../constants/AuthenticationTypeFlag";
import {withStaticAssetCacheBust} from "../../../util/assetCacheBust";
import detailsHtml from './details.html?raw';

export let template = detailsHtml;

type T = IFormTemplate;

export class viewModel {
    private static readonly Heading = 'Skjema';

    private companyLogin!: ICompanyLogin;
    private data!: T;
    private readonly currentPageIndex: KnockoutObservable<number | null> = ko.observable(null);
    private readonly et: ErrorTracker = global.errorAreas.get('form.view.details');
    private readonly formId: string = 'form-' + getNextId();
    private readonly heading: KnockoutObservable<string | null> = ko.observable(null);
    private readonly id = global.getRouteId();
    private readonly isFormSubmitted: KnockoutObservable<boolean | null> = ko.observable(null);
    private readonly isReady: KnockoutObservable<boolean | null> = ko.observable(null);
    private readonly isRedirecting: KnockoutObservable<boolean | null> = ko.observable(null);
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private readonly pages: KnockoutObservableArray<PageData> = ko.observableArray<PageData>([]);
    private readonly preview: boolean = Boolean(Number(global.router.currentRoute()?.__routeParams?.['preview']) ?? false);
    private readonly renderedHtml: KnockoutObservableArray<string> = ko.observableArray<string>([]);
    private readonly req_getcompanylogin = global.getPromiseRequest('getcompanylogin');
    private readonly req_getform: PromiseRequest = global.getPromiseRequest('getform');
    private readonly req_getformauthentication: PromiseRequest = new PromiseRequest('getformauthentication');
    private readonly req_postform: PromiseRequest = new PromiseRequest('postform');
    private readonly requiredAuthType: KnockoutObservable<number | null> = ko.observable(null);
    private readonly showLogin: KnockoutObservable<boolean | null> = ko.observable(null);
    private readonly targetid: number = parseInt(global.router.currentRoute()?.__routeParams?.['targetid'] ?? '0');
    private reqGetSyncfields!: IRequestObject;
    private syncfields!: ISyncField[];
    private __orgnr: string;

    constructor() {
        this.kosub.subscribe(global.login.selectedOrganization, () => this.init());
        this.__orgnr = global.router.currentRoute()?.__routeParams?.['orgnr'] ?? '';
        this.init();
    }

    private init() {
        this.heading(viewModel.Heading);
        this.messageData({message: REQUEST_MESSAGE});
        this.isFormSubmitted(false);
        this.isReady(false);
        Promise.resolve()
            .then(() => this.req_getformauthentication
                .request({
                    id: this.id,
                    orgnr: this.__orgnr,
                })
                .then((data: [number]) => {
                    this.requiredAuthType(data[0]);
                    const authType = this.requiredAuthType();
                    if (authType === null) {
                        return false;
                    }
                    return isBit(global.login.user()?.authenticationtype ?? 0, authType);
                })
            )
            .then((isValidAuthType: boolean) => {
                if (isValidAuthType) {
                    return Promise.resolve()
                        .then(() => this.req_getform
                            .request({
                                id: this.id,
                                targetid: this.targetid,
                                preview: this.preview,
                                orgnr: this.__orgnr ?? undefined,
                            })
                            .then((data: T[]) => {
                                this.data = _.cloneDeep(data[0]);
                                this.data.pages = _.sortBy(this.data.pages, 'order');
                                this.heading(`${viewModel.Heading} [${this.data.name}]`);
                            })
                        )
                        .then(() => {
                            if (!this.reqGetSyncfields) {
                                this.reqGetSyncfields = nsSyncFields.getSyncFields(ObjectTypeId.FormData);
                            }
                            return this.reqGetSyncfields.request
                                .then((data: ISyncField[]) => {
                                    this.syncfields = data;
                                });
                        })
                        .then(() => {
                            this.pages(this.data.pages.map(p => new PageData(this.data, p, this.syncfields)));
                            this.currentPageIndex(0);
                            this.messageData(null);
                            this.isReady(true);
                        })
                        ;
                } else {
                    return Promise.resolve()
                        .then(() => this.req_getcompanylogin
                            .request()
                            .then((data: ICompanyLogin[]) => {
                                const companyLogin = data.find(o => o.orgnr == this.__orgnr);
                                if (!companyLogin) {
                                    throw `Ugyldig organisasjon.`;
                                }
                                this.companyLogin = companyLogin;
                                this.isRedirecting(false);
                                this.messageData(null);
                                this.showLogin(true);
                            })
                        )
                }
            })
            .catch((reason: string) => this.messageData(createErrorMessage(String(reason))));
    }

    private getPage(): PageData | null {
        const cp = this.currentPageIndex();
        if (cp === null || isNaN(cp)) {
            return null;
        }
        return this.pages()[cp] ?? null;
    }

    private getFormPart(id: string): FormAbstract | undefined {
        return this.getPage()?.formParts.find(f => f.id == id);
    }

    private getHtml(): string | null {
        return this.getPage()?.getHtml() ?? null;
    }

    private getFormPartParameters(id: string): any {
        return this.getFormPart(id)?.getFormPartParameters();
    }

    private onCustomSubmit(formPartId: string) {
        const data = this.getSubmitData();
        const fp = this.getFormPart(formPartId);
        if (!fp) {
            return;
        }
        const sf = fp.getSubmitDataButton();
        if (!sf) {
            return;
        }
        if (sf.value) {
            if (validateServerDate(sf.value)) {
                // strip seconds from given value
                sf.value = convertToServerString(convertFromServerString(sf.value)) ?? sf.value;
            }
        } else {
            sf.value = convertToServerString(moment()) ?? '';
        }
        data.push(sf);
        this.submit(data);
    }

    private onSubmit() {
        const data = this.getSubmitData();
        this.submit(data);
    }

    private submit(data: ISyncField[]) {
        this.addFormHtml();
        const formValidity = this.reportFormValidity();
        if (!formValidity) {
            return false;
        }
        this.isReady(false);
        this.messageData({message: `Sender skjema... ${SPINNER}`});
        this.req_postform.abort();
        const html = this.getRenderedHtml().map(encodeURIComponent).join(',');
        this.req_postform
            .request({
                id: this.id,
                targetid: this.targetid,
                formdata: JSON.stringify(data),
                html: html,
            })
            .then(() => {
                this.isFormSubmitted(true);
                this.messageData({message: `Skjema er sendt inn.`});
                const currentPageIndex = this.currentPageIndex();
                if (currentPageIndex !== null && this.pages().length > currentPageIndex + 1) {
                    this.currentPageIndex(currentPageIndex + 1);
                    this.isReady(true);
                }
            })
            .catch((reason: string) => {
                this.isReady(true);
                this.messageData(createErrorMessage(String(reason)));
            });
    }

    private getSubmitData(): ISyncField[] {
        return _(this.pages())
            .map((p: PageData) => p.getSubmitData())
            .flatten()
            .value();
    }

    private onPreviousClick() {
        this.addFormHtml();
        const num = this.currentPageIndex();
        if (num === null) {
            return;
        }
        this.currentPageIndex(num - 1);
    }

    private onNextClick() {
        this.addFormHtml();
        const num = this.currentPageIndex();
        if (num === null) {
            return;
        }
        this.currentPageIndex(num + 1);
    }

    private isPreviousEnabled() {
        const index = this.currentPageIndex();
        return index !== null && index > 0;
    }

    private isNextEnabled() {
        const page = this.getPage();
        if (!page) {
            return false;
        }
        const index = this.currentPageIndex();
        if (index === null) {
            return false;
        }
        const lastPage = index >= this.pages().length - 1;
        const isPreview = this.preview;
        const pageHasButton = !!page.formParts.find(fp => fp.name == 'BUTTON');
        const isValid = page.isValid();
        return !lastPage && (isPreview || (!pageHasButton && isValid));
    }

    private showPrevNext(): boolean {
        return this.pages().length > 1;
    }

    private showNavigation(): boolean {
        return !this.isFormSubmitted();
    }

    private showSubmitButton(): boolean {
        const pageHasButton = !!this.getPagesContainingButtons().length;
        const index = this.currentPageIndex();
        const isLastPage = index !== null && index == this.pages().length - 1;
        return !pageHasButton && isLastPage;
    }

    private enableSubmitButton(): boolean {
        const allPagesValid = this.pages().every(p => p.isValid());
        const isLive = !this.preview;
        return allPagesValid && isLive;
    }

    private enableCustomSubmitButton(): boolean {
        const index = this.currentPageIndex();
        if (index === null) {
            return false;
        }
        const pagesValid = this.pages().reduce((bool: boolean, p: PageData, pageIndex: number) => {
            if (pageIndex > index) {
                return bool;
            }
            return p.isValid() && bool;
        }, true);
        const isLive = !this.preview;
        return pagesValid && isLive;
    }

    private getPagesContainingButtons(): PageData[] {
        return this.pages().filter(p => p.formParts.find(fp => fp.name == 'BUTTON'));
    }

    private reportFormValidity(): boolean {
        const elForm = document.getElementById(this.formId) as HTMLFormElement;
        return elForm.reportValidity?.() ?? true;
    }

    private isLogin(loginType: 'aad' | 'eadm' | 'idporten'): boolean {
        const authType = this.requiredAuthType();
        if (authType === null) {
            return false;
        }
        const _isBit = (type: TAuthenticationType) => isBit(type, authType);
        switch (loginType) {
            case "aad":
                return _isBit(AuthenticationTypeFlag.AAD_FEDERATION);
            case "eadm":
                return _isBit(AuthenticationTypeFlag.EADM_NATIVE) || _isBit(AuthenticationTypeFlag.EADM_NATIVE_SMS);
            case "idporten":
                return _isBit(AuthenticationTypeFlag.ID_PORTEN);
        }
    }

    private onLoginClick(loginType: 'aad' | 'eadm' | 'idporten') {
        switch (loginType) {
            case "aad": {
                this.messageData({message: REDIRECTING_MESSAGE});
                this.isRedirecting(true);
                window.top?.location && (window.top.location.href = `${config.getUrlIdentityHub()}login/aad-${this.__orgnr}/${encodeURIComponent(document.URL)}`)
                break;
            }
            case "eadm": {
                hasher.setHash(`formlogin/${this.__orgnr}`);
                global.companyLogin.setCompanyById(this.__orgnr);
                break;
            }
            case "idporten": {
                this.messageData({message: REDIRECTING_MESSAGE});
                this.isRedirecting(true);
                window.top?.location && (window.top.location.href = `${config.getUrlIdentityHub()}login/idporten-${this.__orgnr}/${encodeURIComponent(document.URL)}`);
                break;
            }
        }
    }

    private getFormHtml(): string | null {
        const formParts = _(this.pages())
            .map((p: PageData) => p.formParts)
            .flatten()
            .filter((fp: FormAbstract) => fp instanceof FormInput)
            .value();
        const orig = document.getElementById(this.formId);
        if (!orig) {
            return null;
        }
        const copy = orig.cloneNode(true) as HTMLFormElement;
        copy.removeAttribute('data-bind');
        copy.querySelectorAll('*').forEach((_el: Element) => _el.removeAttribute('data-bind'));
        copy.querySelectorAll('.buttons-container').forEach(el => el.remove());
        copy.querySelectorAll('input, textarea, select').forEach((_el: Element) => {
            const el = _el as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
            const parentElement = el.parentElement;
            if (!parentElement) {
                return;
            }
            const id = parentElement.id;
            const part = formParts.find((fp: FormInput) => fp.id === id);
            if (!part) {
                return;
            }
            const val = part.getSubmitData()?.value ?? '';
            el.removeAttribute('data-bind');
            switch (el.type) {
                case 'checkbox': {
                    if (val) {
                        el.setAttribute('checked', val);
                    } else {
                        el.removeAttribute('checked');
                    }
                    break;
                }
                case 'radio': {
                    el.removeAttribute('checked');
                    if (id == part.id && el.value == val) {
                        if (val) {
                            el.setAttribute('checked', 'true');
                        } else {
                            el.removeAttribute('checked');
                        }
                    }
                    break;
                }
                case 'textarea': {
                    const textareaEl = document.getElementById(id)?.querySelector('textarea');
                    if (!textareaEl) {
                        return;
                    }
                    textareaEl.removeAttribute('style');
                    el.removeAttribute('style');
                    const cols = textareaEl.cols;
                    const rows = textareaEl.scrollHeight / parseInt(getComputedStyle(textareaEl).lineHeight);
                    el.innerHTML = val;
                    el.setAttribute('cols', String(cols));
                    el.setAttribute('rows', String(rows));
                    break;
                }

                case 'select-multiple': {
                    const values = val.split('|');
                    el.setAttribute('value', val)
                    Array.from((el as HTMLSelectElement).options).forEach(o => {
                        if (values.includes(o.value)) {
                            o.setAttribute('selected', 'true');
                        } else {
                            o.removeAttribute('selected');
                        }
                    })
                    break;
                }
                case 'select-one': {
                    el.setAttribute('value', val)
                    Array.from((el as HTMLSelectElement).options).forEach(o => {
                        if (o.value == val) {
                            o.setAttribute('selected', 'true');
                        } else {
                            o.removeAttribute('selected');
                        }
                    })
                    break;
                }
                default: {
                    el.setAttribute('value', val);
                    break;
                }
            }
        });
        return copy.outerHTML.replace(/<!--.*?-->/gmi, '');
    }

    private getRenderedHtml(): string[] {
        return this.renderedHtml().map((template: string) => {
            return `<!doctype html>
<html lang="no">
<head>
<meta charset="UTF-8">
<title>Skjema</title>
<link rel="stylesheet" href="${withStaticAssetCacheBust(`${window.location.origin}/css/styles.css`)}">
</head>
<body>
    <div class="comp-form-view" style="margin: 1em">
    ${template}
    </div>
</body>
</html>`;
        });
    };

    private addFormHtml() {
        const index = this.currentPageIndex();
        const html = this.getFormHtml();
        if (index === null || html === null) {
            return;
        }
        this.renderedHtml.valueWillMutate?.();
        this.renderedHtml()[index] = html;
        this.renderedHtml.valueHasMutated?.();
    }

    dispose() {
        this.kosub.unsuscribeAll();
        this.req_getform.abort();
        this.reqGetSyncfields?.promise.abort();
        this.pages().forEach(p => p.dispose());
    }
}
